/**
 * ShareBundleModal (Iter 36.j)
 *
 * Two-stage modal for minting a public read-only steering-bundle
 * snapshot link. Mirrors the iter-31 HeatmapShareModal pattern so the
 * surface feels familiar.
 *
 *   stage 1 ("form")    → recipient + label + TTL preset.
 *   stage 2 ("success") → freshly minted /share/bundle/:token URL with
 *                         one-click Copy-to-Clipboard.
 *
 * The list of historical snapshots is rendered separately in
 * BundleSnapshotsList so a consultant can browse old links without
 * dismissing this modal.
 */
import { useState } from "react";
import { toast } from "sonner";
import {
  XIcon as X,
  ShareNetworkIcon as ShareNetwork,
  CopyIcon as Copy,
  CheckIcon as Check,
  LinkIcon as LinkIcon,
} from "@phosphor-icons/react";
import { createBundleSnapshot } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";

const TTL_PRESETS = [
  { days: 7,   label: "7 days" },
  { days: 30,  label: "30 days" },
  { days: 90,  label: "90 days" },
  { days: 365, label: "1 year" },
];

export default function ShareBundleModal({
  workspaceId,
  open,
  onClose,
  onCreated,
}) {
  const [stage, setStage] = useState("form");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientLabel, setRecipientLabel] = useState("");
  const [label, setLabel] = useState("");
  const [ttlDays, setTtlDays] = useState(30);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const fullShareUrl = created
    ? `${window.location.origin}/share/bundle/${created.token}`
    : "";

  const reset = () => {
    setStage("form");
    setRecipientEmail("");
    setRecipientLabel("");
    setLabel("");
    setTtlDays(30);
    setCreated(null);
    setCopied(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const res = await createBundleSnapshot(workspaceId, {
        recipientEmail: recipientEmail.trim() || undefined,
        recipientLabel: recipientLabel.trim() || undefined,
        label: label.trim() || undefined,
        ttlDays,
      });
      setCreated(res);
      setStage("success");
      onCreated?.(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to create snapshot link");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullShareUrl);
      setCopied(true);
      toast.success("Snapshot URL copied to clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't access clipboard — copy the URL manually.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="bundle-snapshot-modal-backdrop"
      onClick={close}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white border border-[var(--ml-border)] max-w-lg w-full shadow-xl"
        data-testid="bundle-snapshot-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--ml-border)]">
          <div className="flex items-center gap-2">
            <ShareNetwork size={16} weight="bold" className="text-[var(--ml-primary)]" />
            <h3 className="font-semibold text-lg">
              {stage === "form" ? "Share steering bundle" : "Snapshot link ready"}
            </h3>
          </div>
          <button
            type="button"
            onClick={close}
            data-testid="bundle-snapshot-modal-close"
            className="ml-btn-icon text-[var(--ml-text-secondary)] hover:text-[var(--ml-text)]"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {stage === "form" && (
          <div className="p-5 space-y-4" data-testid="bundle-snapshot-modal-form">
            <p className="text-sm text-[var(--ml-text-secondary)]">
              Mint a public read-only link to the steering bundle. Anyone with the
              URL can view the latest live composition — no login required. Use a
              recipient email so leaked URLs are traceable.
            </p>

            <div>
              <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1">
                Snapshot label <span className="opacity-50 ml-mono">(optional, your tracking name)</span>
              </label>
              <input
                type="text"
                placeholder="April steering review"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className="ml-input w-full"
                data-testid="bundle-snapshot-modal-label-input"
              />
            </div>

            <div>
              <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1">
                Recipient email <span className="opacity-50 ml-mono">(optional, watermark + audit)</span>
              </label>
              <input
                type="email"
                placeholder="exec@customer.com"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                className="ml-input w-full"
                data-testid="bundle-snapshot-modal-email"
              />
            </div>

            <div>
              <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1">
                Recipient label <span className="opacity-50 ml-mono">(optional)</span>
              </label>
              <input
                type="text"
                placeholder="Jane @ NA Bank — steering rep"
                value={recipientLabel}
                onChange={(e) => setRecipientLabel(e.target.value)}
                className="ml-input w-full"
                data-testid="bundle-snapshot-modal-recipient-label"
              />
            </div>

            <div>
              <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1">
                Expires after
              </label>
              <div className="flex gap-1 flex-wrap" role="radiogroup">
                {TTL_PRESETS.map((p) => (
                  <button
                    key={p.days}
                    type="button"
                    onClick={() => setTtlDays(p.days)}
                    data-testid={`bundle-snapshot-modal-ttl-${p.days}`}
                    className={`px-3 py-1.5 text-sm border ml-mono ${ttlDays === p.days ? "bg-[var(--ml-primary)] text-white border-[var(--ml-primary)]" : "border-[var(--ml-border)] hover:bg-[var(--ml-surface)]/40"}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-[var(--ml-border)] -mx-5 px-5 pt-4">
              <button
                type="button"
                onClick={close}
                className="ml-btn-secondary"
                data-testid="bundle-snapshot-modal-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={submitting}
                data-testid="bundle-snapshot-modal-create-btn"
                className="ml-btn-primary"
              >
                <LinkIcon size={14} weight="bold" /> {submitting ? "Creating…" : "Create snapshot link"}
              </button>
            </div>
          </div>
        )}

        {stage === "success" && created && (
          <div className="p-5 space-y-4" data-testid="bundle-snapshot-modal-success">
            <p className="text-sm text-[var(--ml-text-secondary)]">
              Send this URL to your recipient. It refreshes against the live
              workspace data every time it&apos;s opened and expires on{" "}
              <strong>{new Date(created.expires_at).toLocaleDateString()}</strong>.
              You can revoke it any time from the snapshots list.
            </p>

            <div className="ml-card p-3 bg-[var(--ml-surface)]/40">
              <code
                className="ml-mono text-xs break-all block"
                data-testid="bundle-snapshot-modal-url"
              >
                {fullShareUrl}
              </code>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCopy}
                data-testid="bundle-snapshot-modal-copy-btn"
                className="ml-btn-primary flex-1"
              >
                {copied ? <><Check size={14} weight="bold" /> Copied!</> : <><Copy size={14} weight="bold" /> Copy URL</>}
              </button>
              <a
                href={fullShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-btn-secondary"
                data-testid="bundle-snapshot-modal-preview-link"
              >
                Open preview
              </a>
            </div>

            <button
              type="button"
              onClick={close}
              className="ml-btn-ghost w-full mt-2"
              data-testid="bundle-snapshot-modal-done"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
